import Axios from 'axios';
import FormData from 'form-data';
import each from 'lodash-es/each';
import has from 'lodash-es/has';
import indexOf from 'lodash-es/indexOf';
import isArray from 'lodash-es/isArray';
import isFunction from 'lodash-es/isFunction';
import isNaN from 'lodash-es/isNaN';
import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';
import map from 'lodash-es/map';
import replace from 'lodash-es/replace';
import qs from 'qs';

import {
	all,
	call,
	delay,
	put,
	select as effectSelect,
	take,
	takeEvery,
	takeLatest
} from 'redux-saga/effects';

Axios.defaults.timeout = 0;
Axios.defaults.maxRedirects = 0;
Axios.defaults.withCredentials = true;
Axios.defaults.headers.common.Accept = 'application/json;charset=UTF-8';

const isDownloadable = headers => {
	if (headers['content-type'].indexOf('application/pdf') >= 0) {
		return 'pdf';
	}
	if (headers['content-type'].indexOf('application/vnd.ms-excel') >= 0) {
		return 'xls';
	}
	if (headers['content-type'].indexOf('application/xml') >= 0) {
		return 'xml';
	}
	if (headers['content-type'].indexOf('text/csv') >= 0) {
		return 'csv';
	}
	return false;
};

const paramsSerializer = p => qs.stringify(p, { indices: false });
const FILENAME_CONTENT_DISPOSITION = 'filename=';
const JSONparseArrayBuffer = data => {
	try {
		return JSON.parse(String.fromCharCode.apply(null, new Uint8Array(data)));
	} catch (e) {
		try {
			return JSON.parse(String.fromCharCode.apply(null, new Uint16Array(data)));
		} catch (e2) {
			return String.fromCharCode.apply(null, new Uint8Array(data));
		}
	}
};
const DEBUG = false;

export const defaultAccept = keycloakConfig =>
	function intercept({ method, data, headers, status, ...config }) {
		if (DEBUG) {
			console.log(!status ? 'REQUEST' : 'RESPONSE', {
				method,
				data,
				headers,
				status,
				...config
			});
		}

		const { keycloak } = keycloakConfig || {};
		if (keycloak) {
			headers.Authorization = `Bearer ${keycloak.token}`;
		}

		if (data) {
			// no status means REQUEST
			if (!status) {

				if (isString(data)) {
					return {
						paramsSerializer,
						headers: {
							...headers,
							[method]: {
								...headers[method],
								'Content-Type': 'text/plain'
							}
						},
						method,
						data,
						...config
					};
				}
			} else {
				// status means RESPONSE
				const downloadableType = isDownloadable(headers);
				if (downloadableType) {
					const fileUrl = window.URL.createObjectURL(new Blob([data]));
					const link = document.createElement('a');
					link.href = fileUrl;
					link.id = 'temp-file';

					let name;
					name = headers['content-disposition'];
					if (name && name.indexOf(FILENAME_CONTENT_DISPOSITION) >= 0) {
						name = name.substring(
							name.indexOf(FILENAME_CONTENT_DISPOSITION) +
								FILENAME_CONTENT_DISPOSITION.length
						);
						if (name.startsWith('"')) {
							name = name.substring(1);
						}
						if (name.endsWith('"')) {
							name = name.substring(0, name.length - 1);
						}
					} else {
						name = `dosimetry_file.${downloadableType}`;
					}
					link.setAttribute('download', name);
					document.body.appendChild(link);
					link.click();
					link.remove();
					return {
						data,
						headers,
						fileUrl,
						config
					};
				}
				if (
					config.responseType === 'arraybuffer' &&
					headers['content-type'].indexOf('application/json') >= 0
				) {
					try {
						return {
							paramsSerializer,
							method,
							data: JSONparseArrayBuffer(data),
							headers,
							...config
						};
					} catch (e) {
						console.error(e);
					}
				}
			}
		}
		return {
			paramsSerializer,
			method,
			data,
			headers,
			...config
		};
	};

// DEPRECATED, should be removed when all applications are migrated to keycloak
export const defaultProcessError = function(error) {
	const { url, response } = error || {};
	if (response && response.headers['www-authenticate'] === 'Bearer') {
		const iframe = document.createElement('iframe');
		// a iframe is needed to be able to follow redirect even outside to auth.cern.ch
		iframe.id = 'refresh_frame';
		iframe.style.display = 'none';
		iframe.src = '/me';
		window.document.body.appendChild(iframe);
		iframe.addEventListener('load', () => iframe.remove());
		return new Error('www-authenticate');
	}
	if (response) {
		if (response.status === 401) {
			window.location.replace('/');
		}
		if (response.status === 302) {
			const loc = response.headers.location;
			window.alert('You have been disconnected');
			window.replace(loc);
			// TODO do something to warn user
		}
	}
	// Do something with request error
	return error;
};

export const defaultProcessErrorKeycloak = keycloakConfig => function(error){
	const {response} = error || {};
	const {keycloak, loginOptions} = keycloakConfig || {};
		if(response && response.status ===401){
			if(keycloak){
				keycloak.login(loginOptions)
			}
		}
	return error;
}

export const defaultReject = keycloakConfig => function reject(error) {
	if(keycloakConfig){
		return Promise.reject(defaultProcessErrorKeycloak(keycloakConfig)(error));
	}
	return Promise.reject(defaultProcessError(error));
};

export const defaultPageSize = 10;

const checkParams = (url, params) => {
	const paramRegex = /[^{}]+(?=})/g;
	const requiredKeys = url.match(paramRegex);
	if (requiredKeys && requiredKeys.length > 0) {
		return (
			params &&
			indexOf(
				requiredKeys.map(
					k => has(params, k) && !isUndefined(params[k]) && !isNaN(params[k])
				),
				false
			) === -1
		);
	}
	return true;
};

const urlParamRegex = /[^{}]+(?=})/g;
const getParamRegex = k => new RegExp(`{${k}}`, 'g');

const getUrl = (url, params) => {
	const requiredKeys = url.match(urlParamRegex);
	let result = url;
	each(requiredKeys, k => {
		result = replace(result, getParamRegex(k), encodeURIComponent(params[k]));
	});
	return result;
};

/**
 * Main API creation handler.
 * Will return commonly used methods get,post,put,csv,xml,pdf ...
 * will also add defaultAccept and defaultReject as Axios Interceptor (i.e. handling session loss)
 * */
export const createApiHandler = (axiosConfig, intercept, reject, keycloakConfig) => {
	const axios = Axios.create({
		config: {
			redirect: 'error'
		},
		...axiosConfig
	});
	axios.interceptors.request.use(
		intercept || defaultAccept(keycloakConfig),
		reject || defaultReject(keycloakConfig)
	);
	axios.interceptors.response.use(
		intercept || defaultAccept(keycloakConfig),
		reject || defaultReject(keycloakConfig)
	);

	function safeQuery(
		method,
		url,
		params,
		query,
		body,
		withBody = false,
		config = {}
	) {
		if (checkParams(url, params)) {
			if (withBody) {
				return method(getUrl(url, params), body, {
					params: { ...query },
					...config
				});
			}
			return method(getUrl(url, params), { params: { ...query }, ...config });
		}
		return Promise.reject(new Error('Invalid argument'));
	}

	return {
		_axios: axios,
		_safeQuery: safeQuery,
		get: (url, params, query) => safeQuery(axios.get, url, params, query),
		// Download shorthands

		/**
		 * download a csv
		 * @param url
		 * @param params URL params to replace when running the query
		 * @param query query params
		 * @param method default method is 'get'. see axios methods.
		 * @param body body if post
		 * */
		csv: (url, params, query, method = 'get', body = false) =>
			safeQuery(axios[method], url, params, query, body, !!body, {
				responseType: 'arraybuffer',
				headers: { Accept: 'text/csv;charset=UTF-8' }
			}),
		xml: (url, params, query) =>
			safeQuery(axios.get, url, params, query, false, false, {
				responseType: 'arraybuffer',
				headers: { Accept: 'application/xml;charset=UTF-8' }
			}),
		/**
		 * download a pdf, if a filename is specified then will override the content-disposition
		 * @param url
		 * @param params URL params to replace when running the query
		 * @param query query params
		 * @param method default method is 'get'. see axios methods.
		 * @param body body if post
		 * @param filename if set, will force the filename
		 * */
		pdf: (url, params, query, filename, method = 'get', body = false) =>
			safeQuery(axios[method], url, params, query, body, !!body, {
				responseType: 'arraybuffer',
				headers: { Accept: 'application/pdf;charset=UTF-8' }
			}),

		// with body
		delete: (url, params, query, body) => {
			const data = {data: body}; //axios delete requires body inside a data property, otherwise it does not work
			return safeQuery(axios.delete, url, params, query, data, !!body)
		},
		put: (url, body, params, query) =>
			safeQuery(axios.put, url, params, query, body, true),
		post: (url, body, params, query) =>
			safeQuery(axios.post, url, params, query, body, true),
		patch: (url, body, params, query) =>
			safeQuery(axios.patch, url, params, query, body, true),
		formData: (url, body, params, query) => {
			const form = new FormData();
			Object.keys(body).forEach(key => form.append(key, body[key]));
			return safeQuery(axios.post, url, params, query, form, true);
		},
		// parallel
		getAll: async urls =>
			Promise.all(
				urls.map(({ url, params, query }) =>
					safeQuery(axios.get, url, params, query).catch(() => null)
				)
			)
	};
};

export const watchFor = function* watchFor(
	selector,
	pattern = '*',
	forwardTo,
	mapParams
) {
	while (true) {
		const res = yield take(pattern);
		const params = { ...(mapParams ? mapParams(res) : res) };
		if (yield effectSelect(selector)) {
			yield all(
				map([...forwardTo], type =>
					put({
						...params,
						type
					})
				)
			);
		}
	}
};

export const takePut = function takePut(taken, forwardTo, mapParams) {
	return takeEvery(taken, function* forward(action) {
		const params = { ...(mapParams ? mapParams(action) : action) };
		yield all(
			map(forwardTo, type =>
				put({
					...params,
					type
				})
			)
		);
	});
};

export const takePageQuery = (type, objectOrApiFn) => {
	return takeQuery(type, objectOrApiFn);
};

export const combineSelectors = selectorsArray => params => state => ({
	...selectorsArray
		.map(f => f(params)(state))
		.reduce((current, agg) => ({ ...current, ...agg }))
});

export const applySelect = (select, params) => {
	if (!select) return false;

	if (Array.isArray(select)) {
		return combineSelectors(select)(params);
	}
	return select(params);
};

export function* handleSagaApiCall({ action, api, process, select }) {
	let hasFailed = false;
	let response;
	let data;
	let mustRetry;

	const selectParams = applySelect(select, action.params);
	let { params = {} } = action;

	try {
		if (selectParams) {
			params = yield effectSelect(selectParams);
		}
	} catch (error) {
		hasFailed = error;
		// fast failure here
	}
	////// AXIOS QUERY
	if (!hasFailed) {
		mustRetry = false;
		do {
			hasFailed = false;
			try {
				if (mustRetry) yield delay(mustRetry * 1000);

				if (isArray(api)) {
					response = yield all(api.map(a => call(a, { ...params })));
					data = [];
					response.map(({ d, ...r }) => {
						data.push(d);
						return r;
					});
				} else if (isFunction(api)) {
					response = yield call(api, { ...params });
					const { data: d, ...r } = response;
					data = d;
					response = r;
				} else {
					hasFailed = new Error('Missing api property');
				}
				mustRetry = false;
				data = process ? process(data) : data;
			} catch (error) {
				hasFailed = error;
				// fast failure here
				mustRetry =
					error.message === 'www-authenticate' ? (mustRetry || 0) + 1 : false;
				// see defaultReject to detect session loss
				if (mustRetry !== false)
					console.warn('disconnected, retrying ', mustRetry);
			}
		} while (mustRetry && mustRetry < 3);
	}

	if (!hasFailed && !response?.isAxiosError) {
		const result = {
			request: {
				...action,
				params
			},
			data,
			response
		};
		return { success: true, result };
	}
	const { isAxiosError, response: errResponse } = hasFailed;
	const result = {
		isAxiosError,
		response: errResponse,
		request: {
			...action,
			params
		},
		data: errResponse
			? errResponse.config.responseType === 'arraybuffer' &&
			  errResponse.headers['content-type']?.indexOf('application/json') >= 0
				? JSONparseArrayBuffer(errResponse.data)
				: errResponse.data
			: null
	};
	return { success: false, result };
}

export const takeQuery = (type, objectOrApiFn) => {
	const {
		api,
		process = false,
		effect = takeLatest,
		notify,
		notifyFailure,
		select = false
	} = isFunction(objectOrApiFn) ? { api: objectOrApiFn } : objectOrApiFn;
	return effect(type, function* querySaga(action) {
		const { success, result } = yield call(handleSagaApiCall, {
			action,
			api,
			process,
			select
		});
		const responseActionType = `${type}/${success ? 'SUCCESS' : 'FAILURE'}`;

		yield put({
			type: responseActionType,
			...result
		});

		if (success && notify) {
			yield all(notify.map(a => put(a({ ...result }))));
		}

		if (!success && notifyFailure) {
			yield all(notifyFailure.map(a => put(a({ ...result }))));
		}
	});
};
